import React from "react";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router-dom";

import GroupPopover from "@/components/popovers/group-popover";
import RelativeTimestamp from "@/components/relative-timestamp";
import { Avatar, HStack, Stack, Text } from "@/components/ui";

import type { Account, Group, Status } from "@/schemas";

interface GroupProps {
  account: Account;
  group: Group;
  status: Status;
  avatarSize?: number;
  timestamp?: string;
  timestampUrl?: string;
  action?: React.JSX.Element;
}

const GroupHeader = (props: GroupProps) => {
  const {
    account,
    action,
    avatarSize = 42,
    group,
    status,
    timestamp,
    timestampUrl,
  } = props;

  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    navigate(`/group/${status.group?.slug}`);
  };

  const timestampProps: any = {};
  if (timestampUrl) {
    timestampProps.to = timestampUrl;
    timestampProps.className = "hover:underline";
    timestampProps.onClick = (event: React.MouseEvent) =>
      event.stopPropagation();
  }
  const Timestamp = timestampUrl ? Link : "span";

  return (
    <div
      className="group block w-full shrink-0"
      data-testid="account"
      onClick={handleClick}
      role="link"
    >
      <HStack alignItems="center" justifyContent="between">
        <HStack alignItems="center" space={3}>
          <GroupPopover group={group} isEnabled interaction="hover">
            <span className="relative inline-block">
              <Avatar
                className="size-14 rounded-full"
                src={group.avatar_static}
                size={avatarSize}
              />

              <Avatar
                src={account.avatar_static}
                size={avatarSize / 2}
                className="absolute! right-0 -bottom-1 block ring-3 ring-white dark:ring-gray-900"
              />
            </span>
          </GroupPopover>

          <div className="grow overflow-hidden">
            <HStack space={1} alignItems="center" grow>
              <Text
                size="sm"
                weight="semibold"
                truncate
                dangerouslySetInnerHTML={{ __html: group.display_name_html }}
                data-testid="group-name"
              />
            </HStack>

            <Stack>
              <HStack alignItems="center" space={1}>
                <Link
                  to={`/@${account.username}`}
                  className="hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Text
                    tag="span"
                    theme="muted"
                    size="sm"
                    direction="ltr"
                    truncate
                    data-testid="account-username"
                  >
                    <FormattedMessage
                      id="status.header.group.posted_by"
                      defaultMessage="Posted by @{username}"
                      values={{ username: account.username }}
                    />
                  </Text>
                </Link>

                {timestamp && (
                  <>
                    <Text tag="span" theme="muted" size="sm">
                      &middot;
                    </Text>

                    <Timestamp {...timestampProps}>
                      <RelativeTimestamp
                        timestamp={timestamp}
                        theme="muted"
                        size="sm"
                        className="whitespace-nowrap"
                      />
                    </Timestamp>
                  </>
                )}
              </HStack>
            </Stack>
          </div>
        </HStack>

        {action}
      </HStack>
    </div>
  );
};

export default GroupHeader;
